import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Bot, Sparkles, ArrowLeft, Star, Zap, Shield, Info } from 'lucide-react'
import { enhancedChatService, type ChatMessage, type BirthData } from '../services/enhancedChatService'
import { API_BASE_URL_WITH_VERSION } from '../config/api'

interface EnhancedChatInterfaceProps {
  isOpen: boolean
  onClose: () => void
  birthData?: BirthData
  userId?: string
  title?: string
  subtitle?: string
  placeholder?: string
}

const EnhancedChatInterface = ({ 
  isOpen, 
  onClose, 
  birthData,
  userId = 'default_user',
  title = "Rraasi Enhanced Assistant",
  subtitle = "Powered by Local RAG & Swiss Ephemeris",
  placeholder = "Ask me about your astrological profile..."
}: EnhancedChatInterfaceProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [systemStatus, setSystemStatus] = useState<any>(null)
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    // Initialize chat service when component mounts
    if (birthData && isOpen) {
      enhancedChatService.initialize(userId, birthData)
      checkSystemStatus()
      // Only add welcome message if no messages exist
      if (messages.length === 0) {
        addWelcomeMessage()
      }
    }
  }, [birthData, userId, isOpen]) // Remove messages from dependency to prevent loops

  const checkSystemStatus = async () => {
    try {
      const status = await enhancedChatService.getSystemStatus()
      setSystemStatus(status)
    } catch (error) {
      console.error('Error checking system status:', error)
    }
  }

  // Load existing conversation when component opens
  useEffect(() => {
    if (isOpen && userId && birthData) {
      loadExistingConversation()
    }
  }, [isOpen, userId, birthData])

  const loadExistingConversation = async () => {
    try {
      setIsLoadingConversation(true)
      console.log('🔄 Loading existing conversation for user:', userId)
      
      // Try to get the most recent conversation
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat/recent-conversations?userId=${userId}&limit=1`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.conversations && data.conversations.length > 0) {
          const conversation = data.conversations[0]
          setCurrentConversationId(conversation.id)
          console.log('📚 Found existing conversation:', conversation.id)
          
          // Load messages for this conversation
          await loadConversationMessages(conversation.id)
        } else {
          console.log('🆕 No existing conversations found, starting fresh')
          createNewConversation()
        }
      } else {
        console.log('⚠️ Failed to load conversations, starting fresh')
        createNewConversation()
      }
    } catch (error) {
      console.error('❌ Error loading conversation:', error)
      createNewConversation()
    } finally {
      setIsLoadingConversation(false)
    }
  }

  const loadConversationMessages = async (conversationId: string) => {
    try {
      console.log('📖 Loading messages for conversation:', conversationId)
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat/history?conversationId=${conversationId}&limit=50`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.messages) {
          // Convert backend message format to frontend format
          const loadedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
            id: msg.id || `msg_${Date.now()}_${Math.random()}`,
            type: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content,
            timestamp: new Date(msg.timestamp || msg.createdAt),
            confidence: msg.metadata?.confidence || 1.0,
            sources: msg.metadata?.sources || [],
            astrologicalContext: msg.metadata?.astrologicalContext
          }))
          
          setMessages(loadedMessages)
          console.log('✅ Loaded', loadedMessages.length, 'messages')
        }
      }
    } catch (error) {
      console.error('❌ Error loading conversation messages:', error)
    }
  }

  const createNewConversation = () => {
    const newConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCurrentConversationId(newConversationId)
    console.log('🆕 Created new conversation:', newConversationId)
    setMessages([])
    addWelcomeMessage()
  }

  const addWelcomeMessage = () => {
    const zodiacSign = enhancedChatService.getUserZodiacSign()
    const welcomeMessage: ChatMessage = {
      id: 'welcome',
      type: 'assistant',
      content: `Welcome! I'm your enhanced astrological assistant. I can help you understand your ${zodiacSign} personality, current influences, and provide personalized guidance. What would you like to know?`,
      timestamp: new Date(),
      confidence: 1.0,
      sources: ['Enhanced Chat System'],
      astrologicalContext: {
        zodiacSign,
        personalityTraits: ['Personalized insights available'],
        currentInfluences: ['Real-time astrological data'],
        advice: ['Based on your unique birth chart']
      }
    }
    setMessages([welcomeMessage])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate birth data before allowing chat
    if (!birthData || !birthData.year || !birthData.month || !birthData.day) {
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: '⚠️ Birth data is required for astrological analysis. Please provide your complete birth information including date, time, and location.',
        timestamp: new Date(),
        confidence: 0.0,
        sources: ['System Validation']
      }
      setMessages(prev => [...prev, errorMessage])
      return
    }

    if (inputMessage.trim() && !isLoading) {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: inputMessage.trim(),
        timestamp: new Date()
      }

      // Check if this exact message already exists to prevent duplicates
      const isDuplicate = messages.some(msg => 
        msg.type === 'user' && 
        msg.content === userMessage.content &&
        Date.now() - msg.timestamp.getTime() < 5000 // Within 5 seconds
      )

      if (isDuplicate) {
        console.warn('Duplicate message detected, skipping...')
        return
      }

      setMessages(prev => [...prev, userMessage])
      setInputMessage('')
      setIsLoading(true)

      try {
        // Save user message to backend
        if (currentConversationId) {
          await saveMessageToBackend('user', userMessage.content, currentConversationId)
        }

        // Convert messages to conversation history format
        const conversationHistory = messages.map(msg => ({
          type: msg.type,
          content: msg.content,
          timestamp: msg.timestamp
        }));

        const response = await enhancedChatService.sendMessage(inputMessage.trim(), conversationHistory)
        
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: response.message,
          brief_answer: response.brief_answer,
          detailed_description: response.detailed_description,
          timestamp: new Date(),
          confidence: response.confidence,
          sources: response.sources,
          astrologicalContext: response.astrologicalContext
        }

        setMessages(prev => [...prev, assistantMessage])

        // Save assistant message to backend
        if (currentConversationId) {
          await saveMessageToBackend('assistant', response.message, currentConversationId, {
            confidence: response.confidence,
            sources: response.sources,
            astrologicalContext: response.astrologicalContext
          })
        }
      } catch (error) {
        console.error('Error sending message:', error)
        
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: 'I apologize, but I\'m having trouble processing your request right now. Please try again.',
          timestamp: new Date(),
          confidence: 0.3,
          sources: ['Error handling']
        }

        setMessages(prev => [...prev, errorMessage])
      } finally {
        setIsLoading(false)
      }
    }
  }

  const saveMessageToBackend = async (role: string, content: string, conversationId: string, metadata: any = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No auth token available for saving message');
        return;
      }

      const response = await fetch('/api/chat/save-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role,
          content,
          conversationId,
          metadata: {
            ...metadata,
            timestamp: new Date().toISOString()
          }
        })
      })

      if (response.ok) {
        console.log('✅ Message saved to backend:', { role, conversationId })
      } else {
        console.warn('⚠️ Failed to save message to backend')
      }
    } catch (error) {
      console.error('❌ Error saving message to backend:', error)
    }
  }

  const handleQuickQuestion = async (question: string) => {
    setInputMessage(question)
    // Trigger submit after a short delay to allow state update
    setTimeout(() => {
      const form = document.querySelector('form') as HTMLFormElement
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
      }
    }, 100)
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400'
    if (confidence >= 0.6) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) return <Star className="w-4 h-4" />
    if (confidence >= 0.6) return <Zap className="w-4 h-4" />
    return <Shield className="w-4 h-4" />
  }

  const formatMessageContent = (content: string) => {
    // If content is a JSON string, try to parse and format it
    try {
      const parsed = JSON.parse(content)
      if (typeof parsed === 'object') {
        return Object.entries(parsed)
          .map(([key, value]) => `**${key.replace(/_/g, ' ').toUpperCase()}:** ${value}`)
          .join('\n\n')
      }
    } catch {
      // If not JSON, process as markdown
    }
    
    // Process markdown formatting
    return content
      // Bold text: **text** -> <strong>text</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-purple-300">$1</strong>')
      // Italic text: *text* -> <em>text</em>
      .replace(/\*(.*?)\*/g, '<em class="italic text-gray-300">$1</em>')
      // Headers: ### text -> <h3>text</h3>
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold text-purple-300 mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-purple-300 mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-purple-300 mt-4 mb-2">$1</h1>')
      // Lists: - text -> <li>text</li>
      .replace(/^- (.*$)/gm, '<li class="ml-4 mb-1">• $1</li>')
      // Line breaks
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>')
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        >
          <div className="h-full flex flex-col">
            {/* Enhanced Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900 to-blue-900 border-b border-white/20">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-white" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">{title}</h2>
                  <p className="text-sm text-gray-300">{subtitle}</p>
                  {currentConversationId && (
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-400">Conversation:</span>
                      <span className="text-xs text-purple-300 font-mono bg-purple-900/30 px-2 py-1 rounded">
                        {currentConversationId.substring(0, 8)}...
                      </span>
                      <button
                        onClick={createNewConversation}
                        className="text-xs text-blue-300 hover:text-blue-200 px-2 py-1 rounded hover:bg-blue-900/30 transition-colors"
                      >
                        New Chat
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {isLoadingConversation && (
                  <div className="flex items-center space-x-2 text-xs text-gray-400">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                    <span>Loading conversation...</span>
                  </div>
                )}
                
                {systemStatus && (
                  <div className="flex items-center space-x-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${systemStatus.enhancedChat === 'online' ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span className="text-gray-400">
                      Enhanced: {systemStatus.enhancedChat} | RAG: {systemStatus.localRAG}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-lg p-3 ${
                    message.type === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-800 text-white border border-gray-700'
                  }`}>
                    <div className="flex items-start space-x-2">
                      {message.type === 'assistant' && (
                        <Bot className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {message.brief_answer && (
                          <div className="text-sm font-semibold text-purple-300 mb-3 border-l-2 border-purple-400 pl-3 bg-purple-900/20 p-2 rounded">
                            💫 <strong>Quick Answer:</strong> {message.brief_answer}
                          </div>
                        )}
                        <div 
                          className="text-sm prose prose-invert prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: formatMessageContent(message.detailed_description || message.content) 
                          }}
                        />
                        
                        {/* Enhanced message metadata */}
                        {message.type === 'assistant' && (
                          <div className="mt-2 space-y-1">
                            {message.confidence && (
                              <div className="flex items-center space-x-1 text-xs text-gray-400">
                                {getConfidenceIcon(message.confidence)}
                                <span className={getConfidenceColor(message.confidence)}>
                                  Confidence: {Math.round(message.confidence * 100)}%
                                </span>
                              </div>
                            )}
                            
                            {message.sources && message.sources.length > 0 && (
                              <div className="flex items-center space-x-1 text-xs text-gray-400">
                                <Info className="w-3 h-3" />
                                <span>Sources: {message.sources.join(', ')}</span>
                              </div>
                            )}

                            {message.astrologicalContext && (
                              <div className="mt-2 p-2 bg-purple-900/30 rounded border border-purple-500/30">
                                <div className="text-xs text-purple-300 font-medium mb-1">
                                  Astrological Context
                                </div>
                                <div className="text-xs text-gray-300 space-y-1">
                                  {message.astrologicalContext.zodiacSign && (
                                    <div>Zodiac: {message.astrologicalContext.zodiacSign}</div>
                                  )}
                                  {message.astrologicalContext.personalityTraits && (
                                    <div>Traits: {message.astrologicalContext.personalityTraits.slice(0, 2).join(', ')}</div>
                                  )}
                                  {message.astrologicalContext.currentInfluences && (
                                    <div>Influences: {message.astrologicalContext.currentInfluences.slice(0, 1).join(', ')}</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <div className="flex items-center space-x-2">
                      <Bot className="w-5 h-5 text-purple-400" />
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 1 && (
              <div className="p-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-3">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {enhancedChatService.getChatRecommendations().slice(0, 4).map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickQuestion(question)}
                      className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Form */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={placeholder}
                  disabled={isLoading}
                  className="flex-1 bg-gray-800 text-white placeholder-gray-400 rounded-lg px-4 py-2 border border-gray-600 focus:border-purple-500 focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default EnhancedChatInterface 